import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { useCallback } from 'react'
import log from 'loglevel'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { UserGroupServicePromiseClient } from '@trustero/trustero-api-web/lib/usergroup/usergroup_grpc_web_pb'
import {
  UserGroupID,
  UserGroupRecord,
  UserGroups,
} from '@trustero/trustero-api-web/lib/usergroup/usergroup_pb'
import { useAuthorizedGrpcClientWithContentUpdate } from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'
import { showInfoToast } from 'src/Utils/helpers/toast'

export type UserGroupInput = {
  name: string
  description: string
  labelIds?: string[]
  allowLabelIds?: string[]
  denyLabelIds?: string[]
  memberIds?: string[]
}

// shouldFetch lets callers gated behind the new user roles flag skip the request
// entirely, rather than calling an RPC the environment has not turned on.
export const useUserGroups = (shouldFetch = true): GrpcResponse<UserGroups> => {
  const { response } = useSwrImmutableGrpc(
    UserGroupServicePromiseClient.prototype.list,
    new Empty(),
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

// Every mutation revalidates the same list, so they share one invalidation helper.
const useRevalidateUserGroups = (): (() => Promise<void>) => {
  const { mutate } = useUserGroups()
  const revalidateByMethod = useGrpcRevalidateByMethod()
  return useCallback(async () => {
    await mutate()
    await revalidateByMethod(UserGroupServicePromiseClient.prototype.list)
    await revalidateByMethod(RequestPromiseClient.prototype.listAssigneeGroups)
    await revalidateByMethod(
      RequestPromiseClient.prototype.listDocumentRequests,
    )
  }, [mutate, revalidateByMethod])
}

export const useCreateUserGroup = (): ((
  group: UserGroupInput,
) => Promise<UserGroupRecord>) => {
  const client = useAuthorizedGrpcClientWithContentUpdate(
    UserGroupServicePromiseClient,
  )
  const revalidate = useRevalidateUserGroups()

  return useCallback(
    async (group: UserGroupInput) => {
      const request = new UserGroupRecord()
        .setName(group.name)
        .setDescription(group.description)
        .setLabelIdsList(group.labelIds || [])
        .setAllowLabelIdsList(group.allowLabelIds || [])
        .setDenyLabelIdsList(group.denyLabelIds || [])
        .setUserIdsList(group.memberIds || [])
      try {
        const created = await client.create(request)
        await revalidate()
        return created
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to create user group')
        throw error
      }
    },
    [client, revalidate],
  )
}

export const useUpdateUserGroup = (): ((
  id: string,
  group: UserGroupInput,
) => Promise<UserGroupRecord>) => {
  const client = useAuthorizedGrpcClientWithContentUpdate(
    UserGroupServicePromiseClient,
  )
  const revalidate = useRevalidateUserGroups()

  return useCallback(
    async (id: string, group: UserGroupInput) => {
      // The server replaces the group's whole label and member set, so send the complete list.
      const request = new UserGroupRecord()
        .setId(id)
        .setName(group.name)
        .setDescription(group.description)
        .setLabelIdsList(group.labelIds || [])
        .setAllowLabelIdsList(group.allowLabelIds || [])
        .setDenyLabelIdsList(group.denyLabelIds || [])
        .setUserIdsList(group.memberIds || [])
      try {
        const updated = await client.update(request)
        await revalidate()
        return updated
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user group')
        throw error
      }
    },
    [client, revalidate],
  )
}

export const useDeleteUserGroup = (): ((id: string) => Promise<boolean>) => {
  const client = useAuthorizedGrpcClientWithContentUpdate(
    UserGroupServicePromiseClient,
  )
  const revalidate = useRevalidateUserGroups()

  return useCallback(
    async (id: string) => {
      try {
        await client.delete(new UserGroupID().setId(id))
        await revalidate()
        return true
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to delete user group')
        return false
      }
    },
    [client, revalidate],
  )
}

export const USER_GROUPS_TAB_KEY = 'user-group'
